import { useEffect, useState } from 'react';
import Link from 'next/link';
import { Trophy, Plus, Loader2, Archive, Trash2 } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useAuthStore } from '@/stores/auth.store';
import { authFetch } from '@/lib/auth-client';
import { API_BASE } from '@/lib/api';
import { Button } from '@/components/ui/button';

interface MainEventRow {
  id: string;
  title: string;
  sport: string;
  status: string;
  startTime: string;
  endTime: string;
  entryFee: number;
  _count: { undercards: number; entries: number };
}

const STATUS_COLORS: Record<string, string> = {
  UPCOMING: 'bg-blue-500/10 text-blue-400 border-blue-500/30',
  OPEN: 'bg-green-500/10 text-green-400 border-green-500/30',
  LOCKED: 'bg-amber-500/10 text-amber-400 border-amber-500/30',
  COMPLETED: 'bg-zinc-500/10 text-zinc-400 border-zinc-500/30',
  ARCHIVED: 'bg-zinc-800/50 text-zinc-600 border-zinc-700/50',
};

export default function AdminMainEventsPage() {
  const { accessToken } = useAuthStore();
  const [events, setEvents] = useState<MainEventRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('');

  const fetchEvents = () => {
    if (!accessToken) return;
    setLoading(true);
    const params = new URLSearchParams();
    if (statusFilter) params.set('status', statusFilter);
    fetch(`${API_BASE}/api/admin/main-events?${params}`, {
      headers: { Authorization: `Bearer ${accessToken}` },
    })
      .then((res) => (res.ok ? res.json() : []))
      .then((data) => setEvents(data))
      .catch(() => {})
      .finally(() => setLoading(false));
  };

  const handleArchive = async (id: string, title: string) => {
    if (!confirm(`Archive "${title}"? It will be hidden from users.`)) return;
    const res = await authFetch(`/api/admin/main-events/${id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ status: 'ARCHIVED' }),
    });
    if (res.ok) fetchEvents();
  };

  const handleDelete = async (id: string, title: string, hasEntries: boolean) => {
    const msg = hasEntries
      ? `"${title}" has entries — it will be archived instead of deleted. Continue?`
      : `Permanently delete "${title}"? This cannot be undone.`;
    if (!confirm(msg)) return;
    const res = await authFetch(`/api/admin/main-events/${id}`, { method: 'DELETE' });
    if (res.ok) fetchEvents();
  };

  useEffect(() => {
    fetchEvents();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accessToken, statusFilter]);

  return (
    <div>
      <div className="flex items-center justify-between mb-6 gap-3">
        <div className="flex items-center gap-2 sm:gap-3 min-w-0">
          <Trophy className="h-6 w-6 text-red-400 flex-shrink-0" />
          <h1 className="text-xl sm:text-2xl font-bold text-white truncate">Main Events</h1>
        </div>
        <Link href="/admin/main-events/new" className="flex-shrink-0">
          <Button>
            <Plus className="h-4 w-4 mr-1" />
            <span className="hidden sm:inline">Create Event</span>
            <span className="sm:hidden">Create</span>
          </Button>
        </Link>
      </div>

      {/* Filters */}
      <div className="flex gap-2 mb-6">
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="px-3 py-2 rounded-lg bg-zinc-800 border border-zinc-700 text-white text-sm focus:border-red-500 focus:outline-none"
        >
          <option value="">All Statuses</option>
          <option value="UPCOMING">Upcoming</option>
          <option value="OPEN">Open</option>
          <option value="LOCKED">Locked</option>
          <option value="COMPLETED">Completed</option>
          <option value="ARCHIVED">Archived</option>
        </select>
      </div>

      {loading ? (
        <div className="flex justify-center py-12">
          <Loader2 className="h-6 w-6 text-red-400 animate-spin" />
        </div>
      ) : events.length === 0 ? (
        <div className="text-center py-12 bg-zinc-900 rounded-xl border border-zinc-800">
          <Trophy className="h-12 w-12 text-zinc-600 mx-auto mb-3" />
          <p className="text-zinc-400">No main events found</p>
        </div>
      ) : (
        <div className="bg-zinc-900 rounded-xl border border-zinc-800 overflow-hidden">
          <div className="overflow-x-auto">
          <table className="w-full min-w-[720px]">
            <thead>
              <tr className="border-b border-zinc-800">
                <th className="text-left p-4 text-sm font-medium text-zinc-400">Event</th>
                <th className="text-left p-4 text-sm font-medium text-zinc-400">Sport</th>
                <th className="text-left p-4 text-sm font-medium text-zinc-400">Status</th>
                <th className="text-left p-4 text-sm font-medium text-zinc-400">Dates</th>
                <th className="text-left p-4 text-sm font-medium text-zinc-400">Rounds</th>
                <th className="text-left p-4 text-sm font-medium text-zinc-400">Entries</th>
                <th className="text-right p-4 text-sm font-medium text-zinc-400">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-zinc-800">
              {events.map((event) => (
                <tr key={event.id} className="hover:bg-zinc-800/50">
                  <td className="p-4">
                    <Link
                      href={`/admin/main-events/${event.id}`}
                      className="font-medium text-white hover:text-red-400 transition-colors"
                    >
                      {event.title}
                    </Link>
                    {event.entryFee > 0 && (
                      <span className="ml-2 text-xs text-zinc-500">{event.entryFee} coins</span>
                    )}
                  </td>
                  <td className="p-4 text-sm text-zinc-300">{event.sport}</td>
                  <td className="p-4">
                    <span
                      className={cn(
                        'px-2 py-1 rounded text-xs font-medium border',
                        STATUS_COLORS[event.status] || STATUS_COLORS.UPCOMING
                      )}
                    >
                      {event.status}
                    </span>
                  </td>
                  <td className="p-4 text-sm text-zinc-400">
                    {new Date(event.startTime).toLocaleDateString()} –{' '}
                    {new Date(event.endTime).toLocaleDateString()}
                  </td>
                  <td className="p-4 text-sm text-zinc-300">{event._count.undercards}</td>
                  <td className="p-4 text-sm text-zinc-300">{event._count.entries}</td>
                  <td className="p-4 text-right">
                    <div className="flex items-center justify-end gap-1">
                      {event.status !== 'ARCHIVED' && (
                        <button
                          onClick={() => handleArchive(event.id, event.title)}
                          className="p-1.5 rounded text-zinc-500 hover:text-amber-400 hover:bg-amber-500/10 transition-colors"
                          title="Archive"
                        >
                          <Archive className="h-3.5 w-3.5" />
                        </button>
                      )}
                      <button
                        onClick={() => handleDelete(event.id, event.title, event._count.entries > 0)}
                        className="p-1.5 rounded text-zinc-500 hover:text-red-400 hover:bg-red-500/10 transition-colors"
                        title={event._count.entries > 0 ? 'Archive (has entries)' : 'Delete'}
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        </div>
      )}
    </div>
  );
}
